import {
	Box,
	Button,
	Checkbox,
	Divider,
	Modal,
	ModalAccentIcon,
	ModalActions,
	ModalContent,
	ModalDescription,
	ModalHeading,
	Table,
	TablePagination,
	TBody,
	THead,
	TRow,
	Typography
} from "@launchmetrics/lm-ui";
import { ExclamationCircleOutline, TrashOutline } from "@launchmetrics/lm-icons";

import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { ReactComponent as DeleteIcon } from 'vendor/svg/report/delete.svg';
import { ReactComponent as NoReportsIcon } from 'vendor/svg/report/no_reports.svg';
import { RootState } from "store";
import { addItemToSelection, removeItemFromSelection, setSelectedItems, setDeleteError, addItemsToSelection, removeItemsFromSelection }  from 'store/report/reportManager/reducers';
import { ReportConfigurationModel } from "store/models/reportConfiguration";
import { ReportDropdown } from "components/Article/Report/ReportDropdown";
import { ReportHeader as ReportHeaderType } from "./ReportHeader/types";
import ReportHeader from "./ReportHeader/ReportHeader";
import { orderBy } from "lib/imports/lodash";

import ReportRow from "./ReportRow";
import { useStyles } from "./styles";

const ROWS_PER_PAGE_OPTIONS = [1, 5, 10, 20, 50, 100];

const TABLE_HEADERS: ReportHeaderType[] = [
	{ name: 'name', alias: 'reports.manager.table.name', sortable: true, defaultSort: 'asc', sort: 'name' },
	{ name: 'format', alias: 'reports.manager.table.format', sortable: true, sort: 'type' },
	{ name: 'creation_date', alias: 'reports.manager.table.creation_date', sortable: true, sort: 'inserted_at' },
	{ name: 'date_period', alias: 'reports.manager.table.date_period', sortable: true, sort: 'date_period' },
	{ name: 'actions', alias: 'reports.manager.table.actions', sortable: false }
];

export const ReportManager = () => {

	const [page, setPage] = React.useState(1);
	const [pageSize, setPageSize] = React.useState(ROWS_PER_PAGE_OPTIONS[2]);
	const [reportConfiguration, selectedItems, deleteLoading, deleteError] = useSelector((state: RootState) => [
		state.report.reportManager.items,
		state.report.reportManager.selectedItems,
		state.report.reportManager.deleteLoading,
		state.report.reportManager.deleteError
	]);

	const [itemsToDelete, setItemsToDelete] = React.useState<number[]>([]);
	const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);

	const [sortColumn, setSortColumn] = React.useState<string>('creation_date');
	const [direction, setDirection] = React.useState<'asc' | 'desc'>('desc');

	const parsedReports = React.useMemo(() => reportConfiguration.map(report => ({...report, parsedName: report.name?.toLowerCase() as string})), [reportConfiguration]);
	
	const sortedReports = React.useMemo(() => {
		const header = TABLE_HEADERS.find(header => header.name === sortColumn);
		const selectedHeader = header?.sort === 'name' ? 'parsedName' : header?.sort;
		return orderBy(parsedReports, [selectedHeader, 'parsedName', 'inserted_at'], [direction, 'asc', 'desc']) as unknown as ReportConfigurationModel[];
	}, [direction, parsedReports, sortColumn]);
 
	const styles = useStyles();
	const { t } = useTranslation();

	const dispatch = useDispatch();

	const totalReports = reportConfiguration.length;

	const displayedReports = React.useMemo(() => {
		return sortedReports.slice((page - 1) * pageSize, page * pageSize);
	},[sortedReports, page, pageSize]);
	
	const pageSelectedItems = React.useMemo(() => displayedReports.map(report => report.id!).filter(id => selectedItems.includes(id!)), [displayedReports, selectedItems]);
	const allSelected = React.useMemo(() => displayedReports.length > 0 && (pageSelectedItems.length === displayedReports.length), [pageSelectedItems, displayedReports]);

	React.useEffect(() => {
		return () => {
			dispatch(setSelectedItems([]));
		};
	},[dispatch]);

	React.useEffect(() => {
		if(displayedReports.length === 0 && page > 1) {
			setPage(page - 1);
		}
	},[displayedReports.length, page]);

	const handlePageChange = React.useCallback((value: number) =>{
		setPage(value);
	}, []);

	const handleRowPerPageChange = React.useCallback((value: number) => {
		setPage(1);
		setPageSize(value);
	}, []);

	const handleSortChange = React.useCallback((column: string, direction: 'asc' | 'desc') => {
		setSortColumn(column);
		setDirection(direction);
	}, []);

	const unselectAll = React.useCallback(() => {
		dispatch(setSelectedItems([]));
	},[dispatch]);

	const handleSelectAllChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const checked = event.target.checked;
		if (checked && pageSelectedItems.length === 0) {
			dispatch(addItemsToSelection(displayedReports.map(report => report.id!)));
		} else {
			dispatch(removeItemsFromSelection(pageSelectedItems));
		}
	},[dispatch, displayedReports, pageSelectedItems]);

	const handleSelectRowChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>, reportId: number) => {
		const checked = event.target.checked;
		if (checked) dispatch(addItemToSelection(reportId));
		else dispatch(removeItemFromSelection(reportId));
	}, [dispatch]);

	const handleRowDelete = React.useCallback((reportId: number) => {
		setItemsToDelete([reportId]);
		setDeleteModalOpen(true);
	}, []);

	const handleDeleteSelected = React.useCallback(() => {
		setItemsToDelete(selectedItems);
		setDeleteModalOpen(true);
	},[selectedItems]);

	const handleModalClose = React.useCallback((open: boolean) => {
		setDeleteModalOpen(open);
		if (!open) {
			setItemsToDelete([]);
			dispatch(setDeleteError(false));
		}
	},[dispatch]);

	React.useEffect(() => {
		if (!deleteLoading && !deleteError) {
			setDeleteModalOpen(false);
		}
	},[deleteLoading, deleteError]);

	const handleDelete = React.useCallback(() => {
		dispatch({ type: 'report/deleteReportConfigurations', payload: { ids: itemsToDelete } });
	},[dispatch, itemsToDelete]);

	return (
		<Box id={"report-manager"} styleProps={styles.reportManager}>
			<Box styleProps={styles.reportManagerMainHeader}>
				<Typography variant={"headingSmBold"}>{t('reports.manager.title')}</Typography>
				<ReportDropdown />
			</Box>
			{reportConfiguration.length === 0 ? (
				<Box id={"report-manager-no-reports"} styleProps={styles.noReportsContainer}>
					<NoReportsIcon />
					<Box styleProps={styles.noReportsTextContainer}>
						<Typography variant="headingXsMedium" styleProps={{ color: 'text.default' }}>{t('reports.manager.no_reports.title')}</Typography>
						<Typography variant="textMdRegular" styleProps={{ color: 'text.low', paddingTop: 'spacing-8'}}>{t('reports.manager.no_reports.description')}</Typography>
					</Box>
				</Box>
			) : (
				<>
					<Box styleProps={styles.reportManagerPaginationContainer}>
						<TablePagination
							page={page}
							rowsPerPage={pageSize}
							onPageChange={handlePageChange}
							onRowsPerPageChange={handleRowPerPageChange}
							count={totalReports}
							rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
							id={'tablePagination'}
						/>
					</Box>
					<Box styleProps={styles.reportManagerTableContainer}>
						<Box styleProps={styles.actionsContainer}>
							{selectedItems?.length > 0 ?
								<>
									<Typography variant="textSmSemibold">
										{t('reports.manager.selected_reports')}:&nbsp;
									</Typography>
									<Typography variant="textSmRegular" id="report-manager-selected-reports-nb">
										{selectedItems.length}
									</Typography>
									<Divider orientation="vertical" styleProps={styles.actionDivider} />
									<Button id="report-manager-delete"  variant="textDanger" onClick={handleDeleteSelected} leftIcon={<TrashOutline styleProps={styles.deleteIcon} />}>
										{t('reports.manager.delete')}
									</Button>
									<Button id="report-manager-cancel-selection" variant="textGray" onClick={unselectAll}>{t('reports.manager.cancel_selection')}</Button>
								</>
								:
								<>
									<Typography variant="textSmSemibold">
										{t('reports.manager.total_reports')}:&nbsp;
									</Typography>
									<Typography variant="textSmRegular">
										{totalReports}
									</Typography>
								</>
							}
						</Box>
						<Table 
							id={"reportManagerTable"}
							sortable
							sortColumn={sortColumn}
							sortOrder={direction}
							onSortChange={handleSortChange}
							styleProps={styles.reportManagerTable}
						>
							<THead>
								<TRow>
									<ReportHeader
										key={'checkbox-header'}
										header={{
											alias: (
												<Checkbox
													checked={allSelected}
													size="medium"
													indeterminate={!allSelected && pageSelectedItems.length > 0}
													onChange={handleSelectAllChange}
													id='reportManagerSelectAll'
												/>
											),
											name: 'checkboxHeader',
											sortable: false,
											width: 48
										}}
									/>
									{TABLE_HEADERS.map(header => 
										<ReportHeader
											key={header.name}
											header={{
												...header,
												alias: t(header.alias as string)
											}}
										/>
									)}
									<ReportHeader 
										header={{
											name: 'delete',
											sortable: false,
											alias: '',
											width: 60
										}}
									/>
								</TRow>
							</THead>
							<TBody>
								{reportConfiguration && displayedReports.map((report: ReportConfigurationModel) =>
									<ReportRow
										report={report}
										selected={selectedItems.includes(report.id!)}
										onRowSelect={handleSelectRowChange}
										onRowDelete={handleRowDelete}
										key={report.id}
									/>
								)}
							</TBody>
						</Table>
					</Box>
					<Modal variant="centered" open={deleteModalOpen} onOpenChange={handleModalClose}>
						<ModalContent displayClose={false} styleProps={{ zIndex: 30 }} onPressEnter={handleDelete}>
							<ModalAccentIcon icon={deleteError ? <ExclamationCircleOutline styleProps={{color: 'icon.danger'}} style={{width: 48, height: 48}}/> : <DeleteIcon />} />
							<ModalHeading styleProps={{ h2: { padding: 0 } }} id="report-manager-delete-modal-title">
								{deleteError ? t('reports.manager.delete_modal.heading_error') : t('reports.manager.delete_modal.heading', { count: itemsToDelete.length })}
							</ModalHeading>
							<ModalDescription id="report-manager-delete-modal-description">
								{deleteError ? t('reports.manager.delete_modal.description_error') : t('reports.manager.delete_modal.description')}
							</ModalDescription>
							<ModalActions>
								<Button variant="tertiary" onClick={() => handleModalClose(false)} id="report-manager-delete-modal-cancel-button">
									{t('reports.manager.cancel')}
								</Button>
								<Button
									variant="danger"
									onClick={handleDelete}
									disabled={deleteLoading}
									id="report-manager-delete-modal-delete-button"
								>
									{t('reports.manager.delete')}
								</Button>
							</ModalActions>
						</ModalContent>
					</Modal>
				</>
			)}
		</Box>
	);
};
